import { PropsWithChildren, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import {
  ClassroomFeaturesContextClassroomFeaturePreferencesFieldsFragment,
  ClassroomFeaturesContextClassroomFieldsFragment,
  ClassroomType,
  useClassroomFeaturesContextQuery,
} from 'common/apollo/graphql';
import useAuth from 'common/hooks/useAuth';

const DEFAULT_VALUES = {
  aiTest: {
    enabled: true,
  },
  assignment: {
    enabled: true,
  },
  insights: {
    enabled: true,
  },
  cumulativeRecord: {
    enabled: true,
  },
  jello: {
    enabled: true,
  },
  classtalk: {
    enabled: true,
  },
  memberList: {
    enabled: true,
  },
  classroomSettings: {
    enabled: true,
  },
};

type ClassroomFeaturesPreferences = Omit<
  ClassroomFeaturesContextClassroomFeaturePreferencesFieldsFragment,
  '__typename'
>;

const FeaturesContext = createContext<{
  features: ClassroomFeaturesPreferences | null | undefined;
  classroom: ClassroomFeaturesContextClassroomFieldsFragment | null | undefined;
}>({
  features: DEFAULT_VALUES,
  classroom: { type: ClassroomType.Private },
});

export const useClassroomFeatureEnabled = (
  feature: keyof ClassroomFeaturesPreferences
) => {
  const context = useContext(FeaturesContext);

  if (!context) {
    throw new Error('Context must be used within a Provider');
  }

  return context.features?.[feature]?.enabled ?? false;
};

export const useAvailableClassroomType = (type: ClassroomType) => {
  const context = useContext(FeaturesContext);

  if (!context) {
    throw new Error('Context must be used within a Provider');
  }

  return context.classroom?.type === type;
};

export const ClassroomFeaturesProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const regexp = /classrooms\/(?!new\b|registration\b)([^/]+)/;
  const classroomId = location.pathname.match(regexp)?.[1];

  const { data: { classroom, featurePreferences } = {} } =
    useClassroomFeaturesContextQuery({
      variables: { classroomId: classroomId as string },
      fetchPolicy: 'cache-first',
      skip: !isAuthenticated || !classroomId,
    });

  return (
    <FeaturesContext.Provider
      value={{
        features: featurePreferences,
        classroom,
      }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};
